import { useEffect, useId, useState } from 'react';
import { motion } from 'motion/react';
import type { CSSProperties } from 'react';
import {
  SIXY_PALETTES,
  SIXY_SIZE_PX,
  SIXY_TIMINGS,
  type SixyMood,
  type SixyPalette,
  type SixySize,
} from '../utils/sixyTheme';

/**
 * Sixy — a IA do Six Pass. UMA entidade, DUAS formas:
 *
 *   form='cloud'  → orb de plasma abstrato (presença ambiente, voando livre)
 *   form='robot'  → rosto HUD com olhinhos + sorriso (focada, falando com você)
 *
 * A cloud SEMPRE renderiza no fundo. O robot é um OVERLAY que aparece por cima
 * (fade in/out). Isso permite a transição "nuvem → entra no robô" sem swap
 * abrupto: a cloud fica visível embaixo do robot como o "corpo etéreo" dela.
 *
 * Entry animations:
 *   - 'fly-in': nuvem chega de fora da tela em trajetória curva (1.0s),
 *               então o robot frame monta ao redor (0.5s)
 *   - 'materialize': aparece direto no lugar com fade+scale (0.5s)
 *   - 'none': render imediato
 *
 * Quando `form` muda de 'cloud' → 'robot' em runtime (sem `entry`), faz
 * crossfade suave (0.4s).
 *
 * Refatoração v3:
 *   - Removeu SixyFace e SixyAvatar (eram aliases/wrappers desnecessários)
 *   - Theme extraído pra `sixyTheme.ts`
 *   - Skip turbulence filter em size='sm' (perf budget)
 *   - State machine clara pra transições
 *   - API surface menor: só <Sixy />
 */

interface SixyProps {
  /** Forma visual. Default 'cloud'. */
  form?: 'cloud' | 'robot';
  /**
   * Animação de entrada:
   *   - 'fly-in': voa de fora pra dentro, então monta no robot (se aplicável)
   *   - 'materialize': fade-in no lugar
   *   - 'none': sem animação de entrada
   * Default 'none'.
   */
  entry?: 'fly-in' | 'materialize' | 'none';
  /** Direção de origem do fly-in em graus (0=top, 90=right, 180=bottom, 270=left). Default 315 (top-right). */
  flyFrom?: number;
  /** Distância em px da origem do fly-in. Default 140. */
  flyDistance?: number;
  /** Mood — afeta paleta de cores. Default 'happy'. */
  mood?: SixyMood;
  /** Tamanho. Default 'md'. */
  size?: SixySize;
  /** Direção do olhar (-1 a 1). Default 0. */
  lookAt?: number;
  /** Falando: cloud mostra ripples; robot mostra waveform mouth. */
  talking?: boolean;
  /** Respiração/floating loop. Default true. */
  floating?: boolean;
  /** Classes/estilo no wrapper externo. */
  className?: string;
  style?: CSSProperties;
}

export function Sixy({
  form = 'cloud',
  entry = 'none',
  flyFrom = 315,
  flyDistance = 140,
  mood = 'happy',
  size = 'md',
  lookAt = 0,
  talking = false,
  floating = true,
  className = '',
  style,
}: SixyProps) {
  const px = SIXY_SIZE_PX[size];

  // ===== Entry animation state =====
  // 'pre-entry' → 'flying' → 'arriving' → 'idle'
  type EntryPhase = 'pre-entry' | 'flying' | 'arriving' | 'idle';
  const [entryPhase, setEntryPhase] = useState<EntryPhase>(
    entry === 'none' ? 'idle' : 'pre-entry',
  );

  useEffect(() => {
    if (entry === 'none') {
      setEntryPhase('idle');
      return;
    }
    if (entry === 'materialize') {
      // Fast fade-in
      setEntryPhase('arriving');
      const t = window.setTimeout(() => setEntryPhase('idle'), 500);
      return () => window.clearTimeout(t);
    }
    // fly-in
    setEntryPhase('pre-entry');
    const t1 = window.setTimeout(() => setEntryPhase('flying'), 50);
    const t2 = window.setTimeout(() => setEntryPhase('arriving'), 1050);
    const t3 = window.setTimeout(() => setEntryPhase('idle'), 1550);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [entry]);

  // Calcular offset de origem do fly-in (em px)
  const angleRad = (flyFrom * Math.PI) / 180;
  const flyOffsetX = Math.sin(angleRad) * flyDistance;
  const flyOffsetY = -Math.cos(angleRad) * flyDistance;

  const wrapperAnimate =
    entry === 'fly-in' && entryPhase === 'pre-entry'
      ? { x: flyOffsetX, y: flyOffsetY, opacity: 0, scale: 0.7 }
      : entry === 'fly-in' && entryPhase === 'flying'
        ? { x: 0, y: 0, opacity: 1, scale: 1 }
        : entry === 'materialize' && entryPhase === 'arriving'
          ? { x: 0, y: 0, opacity: 1, scale: 1 }
          : { x: 0, y: 0, opacity: 1, scale: 1 };

  const initialState =
    entry === 'fly-in'
      ? { x: flyOffsetX, y: flyOffsetY, opacity: 0, scale: 0.7 }
      : entry === 'materialize'
        ? { x: 0, y: 0, opacity: 0, scale: 0.5 }
        : { x: 0, y: 0, opacity: 1, scale: 1 };

  // (showRobot foi removido — não era usado no JSX. Decisão de mostrar/esconder
  // robot ficou direto no condicional do render abaixo.)

  // ===== Pet motion =====
  // Movimento de corpo contínuo + ocasional twirl/bounce, varia por mood.
  // Só roda quando o entry terminou (entryPhase==='idle') pra não brigar com
  // a animação de entrada.
  const isIdle = entryPhase === 'idle';
  const petMotion = isIdle && floating ? getPetMotion(mood) : null;

  return (
    <motion.div
      className={className}
      initial={initialState}
      animate={wrapperAnimate}
      transition={{
        type: 'spring',
        stiffness: entry === 'fly-in' ? 90 : 200,
        damping: 18,
        mass: entry === 'fly-in' ? 1.2 : 0.7,
        duration: entry === 'fly-in' ? 1.0 : 0.5,
      }}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: px,
        height: px,
        ...style,
      }}
    >
      {/* Pet motion wrapper — float, sway, twirl ocasional, mood-aware. */}
      <motion.div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          willChange: petMotion ? 'transform' : undefined,
        }}
        animate={petMotion?.animate as Record<string, number[]> | undefined}
        transition={petMotion?.transition}
      >
        {/* Nuvem expressiva — toda a personalidade vem daqui agora.
            form='robot' é tratado como "modo intensificado" (mais FX).
            Mantemos a API mas SEM rosto. */}
        <SixyCloud
          mood={mood}
          size={size}
          lookAt={lookAt}
          talking={talking}
          floating={floating}
          intensified={form === 'robot'}
        />
      </motion.div>
    </motion.div>
  );
}

/**
 * Pet motion presets — comportamento de bichinho.
 *
 * Cada mood tem ritmo próprio:
 *  - excited: agitada, twirl rápido, bounces grandes
 *  - happy: float + sway + twirl ocasional (12s)
 *  - thinking: oscilação suave, sem twirl
 *  - confused: motion errática (tilts random)
 *  - sad: barely moves, lethargic
 *  - error: glitchy small jitters
 *
 * Cada animação combina rotate + x + y + scale numa timeline composta com
 * keyframes alinhadas via `times`. O twirl (360°) acontece em um keyframe
 * específico, depois volta a 0.
 */
function getPetMotion(mood: SixyMood): {
  animate: { rotate?: number[]; x?: number[]; y?: number[]; scale?: number[] };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transition: any;
} {
  switch (mood) {
    case 'excited':
      // Bichinho elétrico: twirl rápido em loop curto, bouncing.
      return {
        animate: {
          rotate: [0, -8, 8, -5, 5, 0, 360, 0],
          y: [0, -6, -2, -5, 0, -3, -6, 0],
          x: [0, 3, -4, 2, -2, 0, 0, 0],
          scale: [1, 1.05, 1.02, 1.06, 1, 1.03, 1.04, 1],
        },
        transition: {
          duration: 5.5,
          repeat: Infinity,
          times: [0, 0.08, 0.18, 0.28, 0.4, 0.55, 0.78, 1],
          ease: 'easeInOut',
        },
      };
    case 'happy':
      // Pet feliz: float suave + twirl ocasional no final do ciclo.
      return {
        animate: {
          rotate: [0, -3, 4, -2, 3, 0, 0, 360, 0],
          y: [0, -5, 1, -3, 2, -4, 0, 0, 0],
          x: [0, 3, -3, 2, -2, 1, 0, 0, 0],
          scale: [1, 1.02, 1, 1.03, 1, 1.02, 1, 1.04, 1],
        },
        transition: {
          duration: 11,
          repeat: Infinity,
          times: [0, 0.08, 0.18, 0.28, 0.38, 0.5, 0.65, 0.82, 1],
          ease: 'easeInOut',
        },
      };
    case 'thinking':
      // Movimento suave de "pensando" — sem twirl, oscilação sutil.
      return {
        animate: {
          rotate: [0, -2, 3, -1, 2, 0],
          y: [0, -3, 1, -2, 0, 0],
          x: [0, 2, -2, 1, -1, 0],
        },
        transition: {
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
        },
      };
    case 'confused':
      // Pet confuso: tilts erráticos, head-jerks.
      return {
        animate: {
          rotate: [0, -6, 4, -3, 5, -4, 2, 0],
          y: [0, -2, 1, -3, 0, 1, -1, 0],
          x: [0, -3, 4, -2, 3, -1, 2, 0],
        },
        transition: {
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        },
      };
    case 'sad':
      // Cabisbaixa, mal se mexe.
      return {
        animate: {
          rotate: [0, -1, 1, 0],
          y: [0, 1, 0],
        },
        transition: {
          duration: 9,
          repeat: Infinity,
          ease: 'easeInOut',
        },
      };
    case 'error':
      // Glitchy — jitter rápido + leve tremor.
      return {
        animate: {
          rotate: [0, -2, 2, -1, 1, 0, -2, 1, 0],
          y: [0, -1, 1, -2, 0, 1, -1, 0, 0],
          x: [0, 2, -2, 1, -1, 0, -1, 1, 0],
        },
        transition: {
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut',
        },
      };
  }
}

// ============================================================================
// CLOUD form — plasma orb expressivo (toda a personalidade vem daqui).
//
// Sem rosto. A expressão de mood é comunicada via:
//   - shape (drooping vertical em sad, esférico em happy/excited)
//   - turbulence intensity (jittery em error, lethargic em sad)
//   - particle count + speed (1 em sad, 5 em excited)
//   - FX overlays (sparkle bursts em excited, warning ripples em error)
//   - color gradient (carrega cor do mood)
//   - ripples (em talking)
// ============================================================================

interface CloudProps {
  mood: SixyMood;
  size: SixySize;
  lookAt: number;
  talking: boolean;
  floating: boolean;
  /** Modo intensificado (form='robot'): mais FX, mais partículas, vibe celebratória. */
  intensified?: boolean;
}

/** Configuração visual por mood — shape, FX, intensidade. */
interface MoodVisuals {
  particleCount: number;
  turbulenceScale: number;
  bodyTransform: string; // CSS transform pra distorcer body shape
  bodyTransformOrigin: string;
  sparkleBursts: boolean;
  warningRipples: boolean;
}

const MOOD_VISUALS: Record<SixyMood, MoodVisuals> = {
  happy: {
    particleCount: 3,
    turbulenceScale: 7,
    bodyTransform: 'none',
    bodyTransformOrigin: 'center',
    sparkleBursts: false,
    warningRipples: false,
  },
  excited: {
    particleCount: 5,
    turbulenceScale: 10,
    bodyTransform: 'scale(1.02)',
    bodyTransformOrigin: 'center',
    sparkleBursts: true,
    warningRipples: false,
  },
  thinking: {
    particleCount: 4,
    turbulenceScale: 5,
    bodyTransform: 'none',
    bodyTransformOrigin: 'center',
    sparkleBursts: false,
    warningRipples: false,
  },
  confused: {
    particleCount: 3,
    turbulenceScale: 8,
    bodyTransform: 'rotate(-3deg)',
    bodyTransformOrigin: 'center',
    sparkleBursts: false,
    warningRipples: false,
  },
  sad: {
    particleCount: 1,
    turbulenceScale: 4,
    bodyTransform: 'scaleY(1.1) translateY(3px)', // drooping
    bodyTransformOrigin: 'center top',
    sparkleBursts: false,
    warningRipples: false,
  },
  error: {
    particleCount: 2,
    turbulenceScale: 9,
    bodyTransform: 'none',
    bodyTransformOrigin: 'center',
    sparkleBursts: false,
    warningRipples: true,
  },
};

function SixyCloud({
  mood,
  size,
  lookAt,
  talking,
  floating,
  intensified = false,
}: CloudProps) {
  const rawId = useId();
  const id = rawId.replace(/[^a-zA-Z0-9]/g, '');
  const palette = SIXY_PALETTES[mood];
  const timing = SIXY_TIMINGS[mood];
  const visuals = MOOD_VISUALS[mood];

  // Perf budget: skip turbulence em size='sm' (40px)
  const useTurbulence = size !== 'sm';

  // Intensified (form='robot'): bumpa particles + ativa sparkles independente do mood
  const particleCount = size === 'sm'
    ? 1
    : intensified
      ? Math.min(5, visuals.particleCount + 2)
      : visuals.particleCount;
  const showSparkleBursts = visuals.sparkleBursts || (intensified && mood === 'happy');

  // Highlight position — segue lookAt
  const highlightCx = 40 - Math.max(-1, Math.min(1, lookAt)) * 6;
  const highlightCy = 38;

  return (
    <motion.div
      animate={floating ? { scale: [1, timing.pulseScale, 1] } : undefined}
      transition={
        floating
          ? {
              duration: timing.pulseDurationSec,
              repeat: Infinity,
              ease: 'easeInOut',
            }
          : undefined
      }
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        style={{
          overflow: 'visible',
          display: 'block',
          transform: visuals.bodyTransform,
          transformOrigin: visuals.bodyTransformOrigin,
          transition: 'transform 0.6s ease-out',
        }}
      >
        <defs>
          <radialGradient id={`plasma-${id}`} cx="48%" cy="42%" r="58%">
            <stop offset="0%" stopColor={palette.core} stopOpacity="0.95" />
            <stop offset="30%" stopColor={palette.mid1} stopOpacity="0.88" />
            <stop offset="65%" stopColor={palette.mid2} stopOpacity="0.6" />
            <stop offset="100%" stopColor={palette.edge} stopOpacity="0" />
          </radialGradient>

          <radialGradient id={`core-${id}`} cx="40%" cy="32%" r="42%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="55%" stopColor={palette.core} stopOpacity="0.35" />
            <stop offset="100%" stopColor={palette.mid1} stopOpacity="0" />
          </radialGradient>

          <radialGradient id={`ambient-${id}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={palette.mid1} stopOpacity="0.55" />
            <stop offset="40%" stopColor={palette.mid2} stopOpacity="0.25" />
            <stop offset="100%" stopColor={palette.edge} stopOpacity="0" />
          </radialGradient>

          {useTurbulence && (
            <filter
              id={`turb-${id}`}
              x="-25%"
              y="-25%"
              width="150%"
              height="150%"
            >
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.028"
                numOctaves="2"
                seed="3"
                result="noise"
              >
                <animate
                  attributeName="seed"
                  from="0"
                  to="200"
                  dur={`${mood === 'thinking' || mood === 'sad' ? 22 : mood === 'excited' || mood === 'error' ? 8 : 14}s`}
                  repeatCount="indefinite"
                />
              </feTurbulence>
              <feDisplacementMap
                in="SourceGraphic"
                in2="noise"
                scale={visuals.turbulenceScale}
                xChannelSelector="R"
                yChannelSelector="G"
              />
              <feGaussianBlur stdDeviation="0.5" />
            </filter>
          )}
        </defs>

        {/* Ambient halo */}
        <circle cx="50" cy="50" r="48" fill={`url(#ambient-${id})`} opacity="0.7" />

        {/* Micro-expressões recorrentes — dão vida CONSTANTE.
            Timings desencontrados pra parecer aleatório, todos respeitando o
            palette do mood. */}
        <MicroExpressions palette={palette} />

        {/* Warning ripples — só em error mood. Vermelho/accent expandindo. */}
        {visuals.warningRipples &&
          [0, 0.7, 1.4].map((delay, i) => (
            <motion.circle
              key={`warn-${i}`}
              cx="50"
              cy="50"
              fill="none"
              stroke={palette.accent}
              strokeWidth="0.6"
              initial={{ r: 30, opacity: 0 }}
              animate={{ r: [30, 50, 54], opacity: [0, 0.6, 0] }}
              transition={{
                duration: 2.1,
                delay,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}

        {/* Ripples quando falando — sempre */}
        {talking &&
          [0, 0.4, 0.8].map((delay, i) => (
            <motion.circle
              key={`talk-${i}`}
              cx="50"
              cy="50"
              fill="none"
              stroke={palette.particle}
              strokeWidth="0.5"
              initial={{ r: 28, opacity: 0 }}
              animate={{ r: [28, 46, 50], opacity: [0, 0.5, 0] }}
              transition={{
                duration: 1.6,
                delay,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}

        {/* Corpo plasma — com turbulence variável por mood */}
        {useTurbulence ? (
          <g filter={`url(#turb-${id})`}>
            <circle cx="50" cy="50" r="34" fill={`url(#plasma-${id})`} />
          </g>
        ) : (
          <circle cx="50" cy="50" r="34" fill={`url(#plasma-${id})`} />
        )}

        {/* Core interno bright */}
        <circle cx="50" cy="50" r="22" fill={`url(#core-${id})`} opacity="0.85" />

        {/* Highlight de luz */}
        <ellipse
          cx={highlightCx}
          cy={highlightCy}
          rx="10"
          ry="6"
          fill="white"
          opacity="0.35"
        />
        <ellipse
          cx={highlightCx - 1.5}
          cy={highlightCy - 1.5}
          rx="3.5"
          ry="2.2"
          fill="white"
          opacity="0.75"
        />

        {/* Sparkle bursts — emanando em 6 direções, escalonadas no tempo.
            Só em excited (ou intensified+happy). Dão vibe celebratória. */}
        {showSparkleBursts &&
          [0, 1, 2, 3, 4, 5].map((i) => {
            const angle = (i * 60 * Math.PI) / 180;
            const cx = 50 + Math.cos(angle) * 42;
            const cy = 50 + Math.sin(angle) * 42;
            return (
              <motion.circle
                key={`spark-${i}`}
                cx={cx}
                cy={cy}
                r="1.6"
                fill={palette.particle}
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: [0, 1.4, 0.6, 0],
                  opacity: [0, 1, 0.5, 0],
                }}
                transition={{
                  duration: 1.6,
                  delay: i * 0.22,
                  repeat: Infinity,
                  repeatDelay: 0.4,
                  ease: 'easeOut',
                }}
                style={{
                  filter: `drop-shadow(0 0 3px ${palette.particle})`,
                }}
              />
            );
          })}

        {/* Particles orbitando — count varia por mood */}
        {particleCount >= 1 && (
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 50 50"
              to="360 50 50"
              dur={`${timing.particleSpeedSec}s`}
              repeatCount="indefinite"
            />
            <circle cx="86" cy="50" r="1.8" fill={palette.particle}>
              <animate
                attributeName="opacity"
                values="0.3;1;0.3"
                dur="2.1s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
        {particleCount >= 2 && (
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="360 50 50"
              to="0 50 50"
              dur={`${timing.particleSpeedSec + 4}s`}
              repeatCount="indefinite"
            />
            <circle cx="50" cy="88" r="1.3" fill={palette.mid1}>
              <animate
                attributeName="opacity"
                values="0.4;1;0.4"
                dur="2.6s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="12" cy="50" r="1" fill={palette.edge}>
              <animate
                attributeName="opacity"
                values="0.4;1;0.4"
                dur="3.1s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
        {particleCount >= 3 && (
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="90 50 50"
              to="450 50 50"
              dur={`${timing.particleSpeedSec + 2}s`}
              repeatCount="indefinite"
            />
            <circle cx="82" cy="76" r="0.9" fill={palette.particle}>
              <animate
                attributeName="opacity"
                values="0.2;0.8;0.2"
                dur="2.8s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
        {particleCount >= 4 && (
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="180 50 50"
              to="-180 50 50"
              dur={`${timing.particleSpeedSec + 5}s`}
              repeatCount="indefinite"
            />
            <circle cx="18" cy="78" r="0.8" fill={palette.core}>
              <animate
                attributeName="opacity"
                values="0.2;0.8;0.2"
                dur="3.4s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
        {particleCount >= 5 && (
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="45 50 50"
              to="405 50 50"
              dur={`${timing.particleSpeedSec - 1}s`}
              repeatCount="indefinite"
            />
            <circle cx="80" cy="22" r="0.7" fill={palette.particle}>
              <animate
                attributeName="opacity"
                values="0.3;1;0.3"
                dur="2.4s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
      </svg>
    </motion.div>
  );
}


// ============================================================================
// MicroExpressions — efeitos recorrentes aleatórios pra dar vida constante.
// Não dependem de mood; rolam em loop independente, dando ar de "ela sempre
// está fazendo algo". Cor vem da paleta do mood atual.
// ============================================================================

function MicroExpressions({ palette }: { palette: SixyPalette }) {
  return (
    <g aria-hidden="true">
      {/* 1. Sparkle wink — 4 pontinhos aparecem em volta, 8s loop.
            Cada um em ângulo diferente, brilho rápido (0.6s) + fade. */}
      {[0, 90, 180, 270].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad) * 44;
        const cy = 50 + Math.sin(rad) * 44;
        return (
          <motion.circle
            key={`wink-${i}`}
            cx={cx}
            cy={cy}
            r="1.5"
            fill={palette.particle}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: [0, 1.3, 0],
              opacity: [0, 0.9, 0],
            }}
            transition={{
              duration: 0.6,
              delay: 2 + i * 0.4,
              repeat: Infinity,
              repeatDelay: 7.4,
              ease: 'easeOut',
            }}
            style={{
              filter: `drop-shadow(0 0 4px ${palette.particle})`,
            }}
          />
        );
      })}

      {/* 2. Bright flare — core brilha forte por meio segundo, a cada 13s.
            Cria sensação de "respirar luz". */}
      <motion.circle
        cx="50"
        cy="50"
        r="18"
        fill={palette.core}
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0, 0.55, 0],
          r: [18, 26, 30],
        }}
        transition={{
          duration: 1,
          repeat: Infinity,
          repeatDelay: 12,
          ease: 'easeOut',
        }}
        style={{
          filter: 'blur(4px)',
        }}
      />

      {/* 3. Ring pulse — anel único expande do centro, 17s loop.
            Mais raro que ripples de "falando", e mais sutil. */}
      <motion.circle
        cx="50"
        cy="50"
        fill="none"
        stroke={palette.mid1}
        strokeWidth="0.5"
        initial={{ r: 26, opacity: 0 }}
        animate={{
          r: [26, 52, 56],
          opacity: [0, 0.45, 0],
        }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          repeatDelay: 15.2,
          ease: 'easeOut',
        }}
      />

      {/* 4. Tiny twinkle — um pontinho minúsculo aparece dentro do corpo,
            posição aleatória, 11s loop. Detalhe sutil. */}
      <motion.circle
        cx="42"
        cy="56"
        r="0.6"
        fill="white"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5] }}
        transition={{
          duration: 0.5,
          repeat: Infinity,
          repeatDelay: 10.5,
          ease: 'easeOut',
        }}
        style={{ filter: 'drop-shadow(0 0 2px white)' }}
      />
      <motion.circle
        cx="62"
        cy="44"
        r="0.6"
        fill="white"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5] }}
        transition={{
          duration: 0.5,
          delay: 4,
          repeat: Infinity,
          repeatDelay: 10.5,
          ease: 'easeOut',
        }}
        style={{ filter: 'drop-shadow(0 0 2px white)' }}
      />
    </g>
  );
}

// ============================================================================
// Re-export types pra compat (não exporta mais SixyAvatar/SixyFace)
// ============================================================================
export type { SixyMood, SixySize } from '../utils/sixyTheme';
